import React, { forwardRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { setChosenLocationNames, setLocationIds } from '../../actions';
import { NewSearch } from '../NewSearch';
import { FallbackView } from '../FallbackView';
import { GeneralAdsPageProps } from './types';
import { getChosenLocationNames } from '../../utils/helpers/getChosenLocationNames';
import { HomeLayoutWithBanner } from '../HomeLayoutWithBanner';
import { capitalizeFirstLetter } from '../../utils/helpers/common';
import { SearchBreadcrumbsContainer, SearchPageTitle } from './styled';
import { ANY_CITY_ID } from '../../utils/helpers/constants';
import { selectSearchFormCurrentCity } from '../../store/selectors';
import { useCityAndLocationsQuery } from '../../hooks/useCityAndLocationsQuery';
import useScreenSize from '../../hooks/useScreenSize';
import { useClient } from '../../hooks/useClient';
import { Breadcrumbs } from '../Breadcrumbs';
import { useSearchResultItems } from '../../hooks/useSearchResultItems';
import { VipItemsSection } from './VipItemsSection';
import { VipResidenceSection } from './VipResidenceSection';
import { RegularItemsSection } from './RegularItemsSection';

const Banner = dynamic(() => import('../Banner'), { ssr: false });
const AdManagerBanner = dynamic(() => import('../Banner/AdManagerBanner'), {
  ssr: false
});

const GeneralAdsPageError = () => {
  const { t } = useTranslation();

  return (
    <FallbackView
      width={317}
      height={230}
      text={t('search.no_results.text')}
      icon="/static/images/new_ad/icons/icon-not-found.png"
    />
  );
};

export const GeneralAdsPage = forwardRef(
  (
    {
      transformedObj,
      setErrorUI,
      h1,
      breadcrumbs,
      isSearchPage,
      isItemsAllPage
    }: GeneralAdsPageProps,
    ref
  ) => {
    const { isSmall } = useScreenSize();
    const router = useRouter();
    const params = useSearchParams();
    const dispatch = useDispatch();
    const isClient = useClient();
    const currentCity: string = useSelector(selectSearchFormCurrentCity);

    const locationIds = useMemo(
      () => params.getAll('location_ids[]'),
      [params.getAll]
    );

    const cityId = currentCity || ANY_CITY_ID;
    const { data } = useCityAndLocationsQuery(cityId);
    const locations = data?.locations || [];

    useEffect(() => {
      if (locationIds.length) {
        const locationNames = getChosenLocationNames(locations, locationIds);
        dispatch(setChosenLocationNames(locationNames));
        dispatch(setLocationIds(locationIds));
      }
    }, [locationIds, data]);

    useEffect(() => {
      router.beforePopState(({ options }) => {
        // eslint-disable-next-line no-param-reassign
        options.scroll = false;
        return true;
      });

      return () => router.beforePopState(() => true);
    }, []);

    const { hasError, vipAllQuery, searchQuery, refetchAllSearchQueries } =
      useSearchResultItems({
        isItemsAllPage,
        transformedObj
      });

    useEffect(() => {
      if (hasError) setErrorUI(true);
    }, [hasError]);

    return (
      <>
        {!isItemsAllPage && (
          <SearchBreadcrumbsContainer>
            <Breadcrumbs
              breadcrumbs={breadcrumbs || []}
              cypress="search-result-breadcrumbs"
            />
          </SearchBreadcrumbsContainer>
        )}
        <NewSearch
          formElToAssign={ref}
          isLoading={vipAllQuery.loading}
          isHomePage={false}
          refetchAllSearchQueries={refetchAllSearchQueries}
        />
        <HomeLayoutWithBanner>
          {h1 && (
            <SearchPageTitle tag="h1" data-cy="search-page-title">
              {capitalizeFirstLetter(h1)}
            </SearchPageTitle>
          )}
          {!searchQuery?.data?.itemsConnection?.totalCount ? (
            <GeneralAdsPageError />
          ) : (
            <>
              <VipItemsSection
                transformedObj={transformedObj}
                searchVippedQuery={vipAllQuery}
              />
              <VipResidenceSection searchVippedResidencesQuery={vipAllQuery} />
              {isSearchPage && isSmall && isClient && (
                <>
                  <Banner
                    key="lotriver-top-banner"
                    containerClassName="lotriver-top-banner"
                    containerId="js-lotriver-top-banner"
                    sid="220686"
                    bn="2"
                    arCn={1}
                  />
                  <AdManagerBanner bannerName={'top'} />
                </>
              )}
              <RegularItemsSection
                {...{ transformedObj, searchQuery, isItemsAllPage }}
              />
            </>
          )}
        </HomeLayoutWithBanner>
      </>
    );
  }
);

GeneralAdsPage.displayName = 'GeneralAdsPage';
